import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PhoneOff, AlertTriangle, Eye, PlusCircle, RotateCcw, ShieldBan } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { cn } from "@/lib/utils";
import { CALLER_META, RESULT_META, CARD, DOT, PILL, inputCls, btnPrimary, btnGhost } from "./shared";

// Détail affiché sous le libellé d'une étape de la cascade.
const stepDetail = (s, { done, current, answered }) => {
  if (s.destination_type === "messagerie" || s.destination_type === "rejet") {
    return current ? "Appel terminé sur cette étape" : "Étape finale";
  }
  if (done) return "Pas de réponse";
  if (current) return answered ? "Appel décroché" : `Sonnerie ${s.ring_seconds || 20} s`;
  return `En attente · ${s.ring_seconds || 20} s`;
};

export default function Simulator() {
  const [number, setNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState(null);
  const [current, setCurrent] = useState(0);
  const [finished, setFinished] = useState(null);
  const [prospectName, setProspectName] = useState("");
  const [prospectCreated, setProspectCreated] = useState(false);

  const reset = () => { setDecision(null); setCurrent(0); setFinished(null); setProspectName(""); setProspectCreated(false); };

  const launch = async () => {
    if (!number.trim()) return;
    setLoading(true); reset();
    try {
      const { data } = await api.post("/telephony/simulate", { phone_number: number });
      setDecision(data);
      if (data.auto_finished) setFinished({ result: data.result, label: data.steps[0]?.label });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };

  const finish = async (result, step) => {
    const tried = decision.steps.slice(0, (step ? step.order : current) + 1);
    const ring = tried.reduce((s, st) => s + (st.ring_seconds || 0), 0);
    try {
      const { data } = await api.post(`/telephony/calls/${decision.call_id}/finish`, {
        result,
        final_destination: step ? `${step.label}${step.user_name ? ` — ${step.user_name}` : step.number ? ` — ${step.number}` : ""}` : null,
        answered_by: step?.user_id || null,
        answered_by_name: step?.user_name || step?.number || null,
        ring_duration: ring,
        call_duration: result === "REPONDU" ? 60 : 0,
      });
      setFinished({ result, label: data.final_destination });
      toast.success("Appel journalisé");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const nextStep = () => {
    const nxt = current + 1;
    if (nxt >= decision.steps.length) return;
    setCurrent(nxt);
    const s = decision.steps[nxt];
    if (s.destination_type === "rejet") finish("REJETE", s);
  };

  // Fait sonner (popup temps réel) l'utilisateur de l'étape courante
  useEffect(() => {
    if (!decision || finished || decision.auto_finished) return;
    const s = decision.steps[current];
    if (s?.destination_type === "user") {
      api.post(`/telephony/calls/${decision.call_id}/ring`, { step_order: s.order }).catch(() => {});
    }
  }, [decision, current, finished]);

  // Détecte une réponse depuis la popup du commercial (synchronisation)
  useEffect(() => {
    if (!decision || finished || decision.auto_finished) return;
    const t = setInterval(() => {
      api.get(`/telephony/calls/${decision.call_id}`).then(({ data }) => {
        if (data.result !== "EN_COURS") {
          setFinished({ result: data.result, label: data.final_destination });
          if (data.answered_by_name) toast.success(`${data.answered_by_name} a répondu depuis sa popup`);
        }
      }).catch(() => {});
    }, 3000);
    return () => clearInterval(t);
  }, [decision, finished]);

  const createProspect = async () => {
    if (!prospectName.trim()) return;
    try {
      await api.post("/companies", { nom: prospectName.trim(), telephone: decision.phone_number, source: "manual" });
      setProspectCreated(true);
      toast.success("Prospect créé avec le numéro appelant");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const company = decision?.company;
  const callerMeta = decision ? CALLER_META[decision.caller_type] : null;
  const resultMeta = finished ? RESULT_META[finished.result] : null;

  return (
    <div className="grid gap-4 items-start grid-cols-[repeat(auto-fit,minmax(320px,1fr))]" data-testid="telephony-simulator">
      {/* Colonne gauche : appel entrant + décision */}
      <div className="flex flex-col gap-3.5 min-w-0">
        <div className={cn(CARD, "p-4")}>
          <div className="text-[15px] font-medium text-slate-900 mb-3">Appel entrant (simulation)</div>
          <div className="flex flex-wrap gap-2.5">
            <input
              data-testid="sim-phone-input"
              className={cn(inputCls, "flex-1 basis-[180px] font-mono")}
              placeholder="06 12 34 56 78"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && launch()}
            />
            <button data-testid="sim-launch-btn" onClick={launch} disabled={loading} className={cn(btnPrimary, "shrink-0")}>
              {loading ? "Routage…" : "Lancer l'appel"}
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-400 text-pretty">
            Le moteur identifie le numéro (client, prospect, inconnu, bloqué) puis applique les règles de routage du manager.
          </p>
        </div>

        {decision && (
          <div className={cn(CARD, "p-4")} data-testid="sim-caller-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="font-mono text-lg font-medium text-slate-900">{decision.phone_number}</span>
              <div className="flex items-center gap-2">
                {decision.watched && (
                  <span className={cn(PILL, "bg-sky-100 text-sky-700")}><Eye size={12} /> Surveillé</span>
                )}
                {decision.blocked ? (
                  <span className={cn(PILL, "bg-rose-100 text-rose-700")}><ShieldBan size={12} /> {decision.flag?.status}</span>
                ) : (
                  <span className={cn(PILL, callerMeta?.cls)} data-testid="sim-caller-type">
                    <span className={cn(DOT, callerMeta?.dot)} /> {callerMeta?.label}
                  </span>
                )}
              </div>
            </div>

            {company && (
              <div className="mt-3.5 flex flex-col gap-1.5 text-[13.5px] text-slate-600">
                <div><span className="text-slate-400">Entreprise :</span> <b className="font-medium text-slate-800">{company.nom}</b></div>
                {company.commercial_name && <div><span className="text-slate-400">Commercial responsable :</span> {company.commercial_name}</div>}
                {decision.contact && <div><span className="text-slate-400">Contact :</span> {decision.contact.prenom} {decision.contact.nom}</div>}
              </div>
            )}

            {decision.anomaly && (
              <div className="mt-3.5 flex gap-2.5 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="sim-anomaly-alert">
                <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
                <span className="text-[13px] text-amber-800 text-pretty">
                  Activité téléphonique anormale : {decision.calls_in_window} appels récents. Alerte envoyée au manager — aucun blocage automatique.
                </span>
              </div>
            )}

            <div className="mt-4">
              <div className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400 mb-2">Pourquoi cette décision</div>
              <ol className="flex flex-col gap-1.5" data-testid="sim-decision-trace">
                {decision.decision_trace.map((t, i) => (
                  <li key={i} className="flex gap-2.5 text-[13.5px] text-slate-600">
                    <span className="font-mono text-indigo-500 shrink-0">{i + 1}.</span>
                    <span className="text-pretty">{t}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>

      {/* Colonne droite : cascade de routage */}
      <div className="min-w-0">
        {decision ? (
          <div className={cn(CARD, "p-4")} data-testid="sim-cascade">
            <div className="text-[15px] font-medium text-slate-900 mb-3.5">Cascade de routage</div>
            <div className="flex flex-col gap-2">
              {decision.steps.map((s, i) => {
                const isCurrent = !finished && i === current;
                const isPast = i < current;
                const isLast = i === current && !!finished;
                const answered = isLast && finished.result === "REPONDU";
                return (
                  <div
                    key={i}
                    data-testid={`sim-step-${i}`}
                    className={cn(
                      "flex items-center gap-3 rounded-xl border-[1.5px] p-3 transition-colors",
                      isCurrent ? "border-indigo-300 bg-indigo-50/50"
                        : answered ? "border-emerald-200 bg-emerald-50/40"
                        : "border-slate-100 bg-white",
                      isPast && "opacity-50"
                    )}
                  >
                    <span className={cn(
                      "h-8 w-8 shrink-0 rounded-full flex items-center justify-center font-mono text-[12.5px] font-medium",
                      isCurrent ? "bg-indigo-600 text-white"
                        : answered ? "bg-emerald-600 text-white"
                        : isPast ? "bg-slate-100 text-slate-400"
                        : "bg-slate-50 text-slate-400"
                    )}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800 truncate">
                        {s.label}{s.user_name ? ` — ${s.user_name}` : s.number ? ` — ${s.number}` : ""}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {stepDetail(s, { done: isPast, current: isCurrent || isLast, answered })}
                      </div>
                    </div>
                    {isCurrent && (
                      <div className="flex gap-1.5 shrink-0">
                        {(s.destination_type === "user" || s.destination_type === "number") && (
                          <>
                            <button data-testid="sim-answer-btn" onClick={() => finish("REPONDU", s)}
                              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700">Répondu</button>
                            <button data-testid="sim-noanswer-btn" onClick={nextStep}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:border-slate-300">
                              <PhoneOff size={12} /> Pas de réponse
                            </button>
                          </>
                        )}
                        {s.destination_type === "attente" && (
                          <button onClick={nextStep}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-medium hover:border-slate-300">Fin d'attente</button>
                        )}
                        {s.destination_type === "messagerie" && (
                          <button data-testid="sim-voicemail-btn" onClick={() => finish("MESSAGERIE", s)}
                            className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium hover:bg-amber-600">Message laissé</button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {finished && (
              <div className="mt-4 pt-4 border-t border-slate-100" data-testid="sim-result">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className={cn(PILL, resultMeta?.cls || "bg-slate-100 text-slate-600")}>
                      <span className={cn(DOT, resultMeta?.dot || "bg-slate-400")} />
                      {resultMeta?.label || finished.result}
                    </span>
                    {finished.label && <span className="text-[13.5px] text-slate-600 truncate">{finished.label}</span>}
                  </div>
                  <button onClick={() => { setNumber(""); reset(); }} className={btnGhost}>
                    <RotateCcw size={14} /> Nouvel appel
                  </button>
                </div>

                {decision.caller_type === "NOUVEAU" && !decision.blocked && !prospectCreated && (
                  <div className="mt-3.5 pt-3.5 border-t border-slate-100">
                    <div className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400 mb-2">Numéro inconnu — créer le prospect ?</div>
                    <div className="flex flex-wrap gap-2.5">
                      <input data-testid="sim-prospect-name" className={cn(inputCls, "flex-1 basis-[180px]")} placeholder="Nom de l'entreprise"
                        value={prospectName} onChange={(e) => setProspectName(e.target.value)} />
                      <button data-testid="sim-create-prospect-btn" onClick={createProspect} className={cn(btnPrimary, "shrink-0")}>
                        <PlusCircle size={15} /> Créer prospect
                      </button>
                    </div>
                  </div>
                )}
                {prospectCreated && (
                  <div className="mt-3 text-sm text-emerald-600">✓ Prospect créé avec le numéro {decision.phone_number}</div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 px-6 py-12 text-center text-[13.5px] text-slate-400 text-pretty">
            Saisissez un numéro puis lancez l'appel pour voir la décision du moteur de routage.
          </div>
        )}
      </div>
    </div>
  );
}
